//************************************************************************ /
// DETAIL VIEW
// Displays the details for an event or a fundraiser on the community board.
// Arrived at when a user clicks an event or fundraiser card on the community board.
//************************************************************************ /

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RightOutlined } from "@ant-design/icons";

import useSWR from "swr";
import { GetStaticPaths, GetStaticProps } from "next";
import { fetcher } from "@dreambigger/shared/src/api/api";
import { browserTimeZone, getCoverImage } from "@dreambigger/shared/src/utils";
import { add, isAfter } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { getDateFnsTimeZone } from "@dreambigger/shared/src/utils";
import {
  eventPaths,
  CreateExternalTransactionParams,
} from "@dreambigger/shared/src/api/events";
import { useTryAsync } from "no-try";
import { organizationPaths } from "@dreambigger/shared/src/api/organizations";
import { communityPaths } from "@dreambigger/shared/src/api/communities";
import { useRouter } from "next/router";
import cotributeDefaults from "@dreambigger/shared/src/assets/cotribute-defaults";
import { removeInspireCookies } from "@dreambigger/shared/src/utils/cookies";
import useUser from "@dreambigger/shared/src/hooks/use-user";
import {
  Event as EventType,
  Community,
  OrgBootstrap,
} from "@dreambigger/shared/src/types";
import {
  Head,
  message,
  Spin,
  Form,
  Button,
  NextAndPreviousBar,
  Input,
  Checkbox,
  Card,
  Divider,
  ShareBar,
  InputNumber,
  Group,
  Typography,
} from "@dreambigger/design-system/src/components/";
import {
  api,
  tokenHelper,
  communityApi,
  orgApi,
  eventApi,
} from "@dreambigger/shared/src/api/weserve/api-helper";
import InspireAnalytics, {
  InspireAnalyticEvents,
} from "@dreambigger/shared/src/utils/inspire-analytics-helper";
import { useEventAnalytics } from "../../hooks";
import StripeCardPayment from "../ui/stripe-card-payment";
import { removeLogoutLocalStorageItems } from "@dreambigger/shared/src/utils/local-storage";
import { zipCodeValidationRule } from "../../util/validation";
import Decimal from "decimal.js";

export type EventProps = {
  event: EventType;
  orgBootstrap: OrgBootstrap;
  community: Community;
};

export interface DonationForm {
  amount: number;
  hideAmount?: boolean;
  firstName: string;
  lastName: string;
  hideName?: boolean;
  email: string;
  zipCode?: string;
  donationNote?: string;
  optIn1?: boolean;
  optIn2?: boolean;
  companyName?: string;
}

const MAX_TEXT_AREA_LENGTH = 200;

// -----------------------------------------------------------------------------------------------------------------
//   ASYNC EXPORT STATIC GENERATION FUNCTIONS
//   * These functions are currently called in @dreambigger/cotribute-shell/events/[eventId]/detail.tsx
//     and allow us to pass in orgBootstrap, event, eventStories, and community as props to the Event component below.
// -----------------------------------------------------------------------------------------------------------------

// Get static paths
export const getEventDonateStaticPaths: GetStaticPaths = async () => {
  const res = await api.get("/active-event-ids");
  const eventIds: number[] = res.data.eventIds;
  //   Only statically generate the last X amount of events. Default to 300
  const staticPageLimit = process.env.ENTITY_STATIC_PAGE_LIMIT || 300;

  return {
    paths: eventIds
      .slice(0, Math.min(Number(staticPageLimit), eventIds.length))
      .map((id) => ({ params: { eventId: String(id) } })),
    fallback: true,
  };
};

// Get static props to be passed into Donate component.
export const getEventDonateStaticProps: GetStaticProps = async ({ params }) => {
  const eventId = params!.eventId as string;
  // *** Get event data.
  const [eventError, eventRes] = await useTryAsync(() =>
    eventApi.getById(Number(eventId))
  );
  if (eventError) {
    return {
      notFound: true,
    };
  }

  // Assign results to variables.
  const event: EventType = eventRes!.data;

  // Get orgBootstrap data.
  const orgBootstrapRes = await orgApi.getOrgBootstrap(event.organization.id);
  const orgBootstrap = orgBootstrapRes.data;
  // Return all constants as props.
  const returnProps = {
    event,
    orgBootstrap,
  } as EventProps;

  // Get community data
  const [communityError, communityResponse] = await useTryAsync(() =>
    communityApi.getBySlug(orgBootstrap?.slug)
  );
  // Add community data to returnProps
  if (!communityError && communityResponse?.data) {
    returnProps.community = communityResponse.data;
  }
  // Return the returnProps, revalidating every 120 seconds.
  return {
    props: returnProps,
    revalidate: 120,
  };
};

const EventDonate = ({
  orgBootstrap: initialOrgBootstrap,
  event: initialEvent,
  community: initialCommunityData,
}: EventProps) => {
  const router = useRouter();
  const { TextArea } = Input;

  // Keep track of the current step of the form the user is on.
  const totalSteps = 3;
  const [step, setStep] = useState(1);

  // Move user to the next step of the form if they aren't on the last step.
  const handleNext = useCallback(() => {
    setStep((prev) => (prev < totalSteps ? prev + 1 : prev));
  }, []);
  const handlePrevious = useCallback(() => {
    setStep((prev) => (prev > 1 ? prev - 1 : prev));
  }, []);
  const handleSuccess = useCallback(() => {
    setStep(3);
  }, []);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const updatePaymentComplete = (value: boolean) => {
    setPaymentComplete(value);
  };
  //keep track of amount button that is clicked for styling
  const [activeButton, setActiveButton] = useState<number | null>(null);
  const buttonClass = (value: number | null): string =>
    activeButton != null && Number(activeButton) === Number(value)
      ? "bg-primary b-primary white"
      : "b-primary primary";
  //handle input value of amount field when button is clicked.
  const handleButtonClick = (value: number) => {
    const numValue = Number(value);
    form.setFieldsValue({ amount: numValue });
    setActiveButton(numValue); // Update the state with the button that is clicked
    // Re-validate amount to clear any stale errors, then check overall form validity
    form.validateFields(["amount"]).finally(() => checkStep1Validity());
    googleAnalytics.event({
      category: "Donate Page - Donation Amount",
      action: "Button Click",
      label: `${numValue}`,
    });
    InspireAnalytics.track({
      eventName: InspireAnalyticEvents.DONATION_AMOUNT_CLICK,
      properties: {
        Page: window.location.pathname,
        "Donation Amount": numValue,
        "Event ID": initialEvent?.id,
      },
      mixpanel,
      segment,
    });
  };

  const handleFundraiserClick = useCallback(() => {
    router.push(`/events/${initialEvent?.id}/detail`);
  }, [router, initialEvent?.id]);

  // Store the currently logged-in user, if present.
  const [userLoading, { data: user }] = useUser(api, tokenHelper);

  // Use Form
  const [formValues, setFormValues] = useState<DonationForm>();
  const [form] = Form.useForm();
  const [isStep1Valid, setIsStep1Valid] = useState(false);
  const [balanceOnlyLoading, setBalanceOnlyLoading] = useState(false);
  const checkStep1Validity = () => {
    const values = form.getFieldsValue();
    const zipRequired = event?.organization?.meta?.zipCodeStepOne;
    const requiredFilled =
      Number(values.amount) >= 5 &&
      values.firstName &&
      values.lastName &&
      values.email &&
      (!zipRequired || values.zipCode);
    setIsStep1Valid(!!requiredFilled);
  };

  const onFinish = (values: DonationForm) => {
    setFormValues(values);
    handleNext();
    googleAnalytics.event({
      category: "Donate Page - Donation Amount",
      action: "Next Button Click",
      label: `${values.amount}`,
    });
  };

  const initialValues = {
    email: user?.email ? user?.email : "",
    firstName: user?.firstName ? user?.firstName : "",
    lastName: user?.lastName ? user?.lastName : "",
  };

  // Fetch event metadata to compare against statically generated data
  const { isFallback } = useRouter();
  const fetcherFn = (url: string) => fetcher(api, tokenHelper, url);

  const { data: eventData } = useSWR<EventType, any>(
    initialEvent?.id ? eventPaths.getById(initialEvent.id) : null,
    fetcherFn,
    {
      fallbackData: initialEvent,
      onError: (error) => {
        message.error("Something went wrong loading this event.");
      },
    }
  );

  // Fetch orgBootstrap metadata to compare against statically generated data
  const { data: orgBootstrapData } = useSWR<OrgBootstrap, any>(
    initialEvent?.organization.id
      ? organizationPaths.getOrgBootstrapPath(initialEvent.organization.id)
      : null,
    fetcherFn,
    {
      fallbackData: initialOrgBootstrap,
    }
  );
  // Fetch community metadata to compare against statically generated data
  const { data: communityData } = useSWR<Community, any>(
    initialEvent?.organization.community?.slug
      ? communityPaths.getBySlug(initialEvent.organization.community.slug)
      : null,
    fetcherFn,
    {
      fallbackData: initialCommunityData,
    }
  );

  // Assign metadata variables using fetched data, if available, or default to using the static data.
  const orgBootstrap = orgBootstrapData || initialOrgBootstrap;
  const event = eventData || initialEvent;
  const community = communityData || initialCommunityData;

  // Redirect if donation buffer has expired (endAt + buffer days)
  const eventEndDateBuffer =
    event?.organization?.meta?.eventEndDateBuffer > -1
      ? event.organization.meta.eventEndDateBuffer
      : 10;

  const donationBufferExpired = useMemo(() => {
    const timeZone = event?.timeZone;
    if (!timeZone || !event?.endAt) return false;
    const dateFnsTimezone = getDateFnsTimeZone(timeZone);
    const endDatePlusBuffer = add(new Date(event.endAt), {
      days: eventEndDateBuffer,
    });
    const now = new Date();
    const formattedNow = formatInTimeZone(now, dateFnsTimezone, "yyyy-MM-dd");
    const formattedEnd = formatInTimeZone(
      endDatePlusBuffer,
      dateFnsTimezone,
      "yyyy-MM-dd"
    );
    return isAfter(new Date(formattedNow), new Date(formattedEnd));
  }, [event?.endAt, event?.timeZone, eventEndDateBuffer]);

  useEffect(() => {
    if (donationBufferExpired) {
      router.replace(`/events/${event?.id}/detail`);
    }
  }, [donationBufferExpired, router, event?.id]);

  // Balance / split payment calculation (use Decimal to avoid floating-point errors on monetary values)
  const canUseBuckets =
    orgBootstrap?.externalMoneyBuckets &&
    !!user?.balanceToSpend &&
    user.balanceToSpend > 0;
  const balanceToSpend = user?.balanceToSpend || 0;
  const donationAmount = formValues?.amount || 0;
  const donationFromBalance = canUseBuckets
    ? Decimal.min(donationAmount, balanceToSpend).toNumber()
    : 0;
  const donationFromCC = new Decimal(donationAmount)
    .minus(donationFromBalance)
    .toNumber();
  const isBalanceOnly =
    canUseBuckets && donationFromCC === 0 && donationAmount > 0;

  // Handle balance-only donation submission (no Stripe needed)
  const handleBalanceOnlySubmit = useCallback(async () => {
    if (!formValues || !event) return;
    setBalanceOnlyLoading(true);
    try {
      const externalTransactionPayload: CreateExternalTransactionParams = {
        guest: {
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          email: formValues.email,
          zip: formValues.zipCode || null,
          company: formValues.companyName || "",
          donorAddress: null,
          // The donor's IANA time zone, so an export can render this
          // donation's timestamps on their own clock rather than in UTC.
          // Undefined where the browser cannot report one; the API stores
          // nothing in that case and consumers fall back to UTC.
          timeZone: browserTimeZone(),
        },
        transactions: [
          {
            isAmountPublic: !formValues.hideAmount,
            isPublic: !formValues.hideName,
            product: {
              id: event.campaign?.id,
              price: new Decimal(formValues.amount).toFixed(2),
            },
            description: event.name,
            type: "donation",
            quantity: 1,
          },
        ],
        comments: formValues.donationNote || "-",
        stripe: null,
        flags: {
          fundraiserNotifications: formValues.optIn1 || false,
          relatedEventNotifications: formValues.optIn2 || false,
          event_id: event.id,
        },
      };
      await eventApi.createExternalTransaction(externalTransactionPayload);
      updatePaymentComplete(true);
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { error?: string; message?: string } };
      };
      const errorMessage =
        axiosError?.response?.data?.error ||
        axiosError?.response?.data?.message ||
        "Failed to save donation. Please try again.";
      message.error(errorMessage);
    } finally {
      setBalanceOnlyLoading(false);
    }
  }, [formValues, event, updatePaymentComplete]);

  // Track page views and user analytics (extracted hook with proper dependencies)
  const { mixpanel, segment, googleAnalytics } = useEventAnalytics({
    event,
    community,
    orgBootstrap,
    user,
    userLoading,
  });

  // Guard to prevent duplicate analytics on payment completion
  const completionTrackedRef = useRef(false);

  // Handle payment completion - redirect to success screen or fundraiser page
  useEffect(() => {
    if (paymentComplete && !completionTrackedRef.current) {
      completionTrackedRef.current = true;
      googleAnalytics.event({
        category: "Donate Page - Billing Details",
        action: "Donation Complete",
        label: `${event.id} - ${formValues?.amount}`,
      });
      InspireAnalytics.track({
        eventName: InspireAnalyticEvents.DONATION_COMPLETE,
        properties: {
          Page: window.location.pathname,
          "Event ID": event.id,
          "Donation Amount": formValues?.amount,
        },
        mixpanel,
        segment,
      });

      // People aggregates: revenue + last-donation timestamps + zzBMVP1 counter.
      // Property names mirror legacy donationsctrl.js / appdonationsctrl.js
      // exactly so historical Mixpanel reports keep updating in place:
      //   - "Total Donations" / "Total Donated" — running cumulative donation
      //     total (legacy passed user.spentOnDonations directly; we add the
      //     fresh donation to the loaded balance since the user object isn't
      //     re-fetched here).
      //   - "Last Donation TS" — last donation timestamp.
      //   - "zzBMVP1: Total Donations Count" — legacy increments by donation
      //     amount, NOT by 1 (preserved here verbatim despite the misleading
      //     "Count" name).
      // `amount` is typed `number`, but the form pulls user input — guard
      // against NaN/Infinity so a malformed entry can't poison the counter.
      const donationAmt = formValues?.amount ?? 0;
      if (Number.isFinite(donationAmt) && donationAmt > 0) {
        const nowTs = +new Date();
        const cumulativeDonated = (user?.spentOnDonations || 0) + donationAmt;
        InspireAnalytics.trackCharge({
          mixpanel,
          amount: donationAmt,
          properties: {
            Type: "Donation",
            "Event ID": event.id,
            "Event Name": event.name,
          },
        });
        InspireAnalytics.setPeopleProperties({
          mixpanel,
          properties: {
            "Total Donations": cumulativeDonated,
            "Total Donated": cumulativeDonated,
            "Last Donation TS": nowTs,
          },
        });
        // Legacy semantics: increments by donation $ amount, not by 1.
        InspireAnalytics.incrementPeopleProperty({
          mixpanel,
          property: "zzBMVP1: Total Donations Count",
          value: donationAmt,
        });
      }
      if (
        event.campaign?.thresholdTrigger === "one_to_one_match_trigger" ||
        event.campaign?.thresholdTrigger === "donation_trigger" ||
        event.campaign?.thresholdTrigger === "tiered_match_trigger"
      ) {
        handleSuccess(); // Redirect to success screen
      } else {
        window.location.href = `/events/${initialEvent?.id}/detail?donation=success`;
      }
    }
  }, [
    paymentComplete,
    event.campaign?.thresholdTrigger,
    handleSuccess,
    router,
    initialEvent?.id,
    googleAnalytics,
    event.id,
    formValues?.amount,
    mixpanel,
    segment,
  ]);

  const handleLogoutClick = useCallback(() => {
    removeLogoutLocalStorageItems();
    removeInspireCookies();
    InspireAnalytics.reset({
      segment,
      mixpanel,
    });
    router.reload();
  }, [segment, mixpanel, router]);

  const handleShareClick = useCallback(
    (shareType: string) => {
      eventApi.trackSocialShare(event.id, shareType);
    },
    [event.id]
  );

  const shareAnalyticsProperties = useMemo(
    () => ({
      "Event Type": event.isFundraiser ? "fundraiser" : "event",
      "Event ID": event.id,
      "Event Name": event.name,
      groupId: event.organization.id,
      "Group Name": event.organization.name,
    }),
    [
      event.id,
      event.name,
      event.isFundraiser,
      event.organization.id,
      event.organization.name,
    ]
  );

  const hasImages = (event?.images && event?.images.length > 0) || undefined;
  const coverImage =
    (hasImages && getCoverImage(event.images)) || cotributeDefaults.coverImage;

  //   Prepare for final render. Check if page has been statically rendered. If not (such as when using yarn run dev),
  //   display loading spinner while data is fetched.
  if (isFallback || userLoading) {
    return <Spin style={{ width: "100%", marginTop: 150 }} />;
  }

  // Guard against scenario where no data is found.
  if (!event) {
    message.error("Error loading page");
    return null;
  }

  const headMeta = {
    favicon: community?.meta?.brand?.tabIcon || cotributeDefaults.tabIconIco,
    title: event.name || "Event",
    description: "Check out how much fun we had making a difference together!",
    siteName: "Events",
    image: coverImage,
  };

  const sponsorLogo = community?.meta?.brand?.logo || "";
  const donationLogo =
    community?.organization?.meta?.externalCampaign?.donationFeesCoverageLogo ||
    null;
  const donationsFeesCoverage =
    community?.organization?.meta?.externalCampaign?.donationFeesCoverage;
  const showMatch =
    !!event?.campaign?.matchFundsRemaining || !!donationsFeesCoverage;
  const matchLogo = donationLogo || sponsorLogo || null;
  const hasDonationValues =
    community?.organization?.meta?.externalCampaign?.defaultDonation1 &&
    community?.organization?.meta?.externalCampaign?.defaultDonation2 &&
    community?.organization?.meta?.externalCampaign?.defaultDonation3 &&
    community?.organization?.meta?.externalCampaign?.defaultDonation4;

  const { Title, Text } = Typography;

  // **************************************************************
  // * RENDER THE VIEW
  // **************************************************************
  return (
    <>
      <Head {...headMeta} />
      <style>{`
        .donation-amount-input .ant-input-number-input {
          color: rgba(0, 0, 0, 0.88) !important;
        }
      `}</style>
      <div className="default-wrapper-padding pv-6">
        {/* Grab Donation Amount and Personal Info */}
        {step === 1 && (
          <Card
            className="w-100 md_w-50"
            style={{
              margin: "0 auto",
              boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
            }}
          >
            <NextAndPreviousBar
              nextText="Next"
              centerText="Donation Amount"
              actionTextClass="f-4 fwb primary"
              centerTextClass="f-6 fwb items-center"
              className=""
              previousText="Cancel"
              showRight={true}
              previousFunction={handleFundraiserClick}
              nextFunction={() => form.submit()}
            />
            <Divider className="mt-3 mb-3" />
            {event.campaign?.thresholdTrigger ===
              "one_to_one_match_trigger" && (
              <div className="bg-primary mb-5 pa-3 white flex items-center justify-center br-1">
                <Text className="f-5 white">
                  Every donation matched
                  <span className="f-6 fwb">&nbsp;$1 for $1&nbsp;</span>by{" "}
                  {orgBootstrap.name}!
                </Text>
              </div>
            )}
            {event.campaign?.thresholdTrigger === "donation_trigger" &&
              event.campaign.nextThreshold && (
                <div className="bg-primary mb-5 pa-3 white flex items-center justify-center br-1">
                  <Text className="f-5 white">
                    Donate to help unlock{" "}
                    <span className="f-6 fwb">
                      ${event.campaign.nextThreshold.reward.toLocaleString()}
                    </span>{" "}
                    in match grants!
                  </Text>
                </div>
              )}
            {canUseBuckets && (
              <div className="bg-green mb-5 pa-3 white flex items-center justify-center br-1">
                <Text className="f-5 white">
                  Available Funds to Donate:&nbsp;
                  <span className="f-6 fwb">
                    ${new Decimal(balanceToSpend).toFixed(2)}
                  </span>
                </Text>
              </div>
            )}
            <Title level={5} className="tc mb-4">
              Make Donation to {event.name}
            </Title>
            <Form
              form={form}
              onFinish={onFinish}
              initialValues={initialValues}
              onFieldsChange={() => checkStep1Validity()}
            >
              <Group spacing={4} className="ph-3">
                <Group
                  xs="horizontal"
                  spacing={3}
                  className="justify-around pv-3"
                >
                  {(hasDonationValues
                    ? [
                        community?.organization?.meta?.externalCampaign
                          ?.defaultDonation1,
                        community?.organization?.meta?.externalCampaign
                          ?.defaultDonation2,
                        community?.organization?.meta?.externalCampaign
                          ?.defaultDonation3,
                        community?.organization?.meta?.externalCampaign
                          ?.defaultDonation4,
                      ]
                    : [10, 25, 50, 100]
                  ).map((amount) => (
                    <Button
                      key={amount}
                      onClick={() => handleButtonClick(amount as number)}
                      className={`f-5 ${buttonClass(amount as number)}`}
                      style={{ borderRadius: "9999px", minWidth: "70px" }}
                    >
                      ${amount}
                    </Button>
                  ))}
                </Group>
                <Text className="db tc fwb pb-3">
                  or enter another amount below:
                </Text>
                <Form.Item
                  name="amount"
                  rules={[
                    {
                      validator: (_, value) =>
                        value && Number(value) >= 5
                          ? Promise.resolve()
                          : Promise.reject(
                              "Donation Amount must be at least $5"
                            ),
                    },
                  ]}
                >
                  <InputNumber
                    className="w-100 donation-amount-input"
                    addonBefore="$"
                    addonAfter=".00"
                    min={0}
                    precision={0}
                    step={1}
                    size="large"
                    status=""
                    onChange={(value) => {
                      setActiveButton(value);
                      setTimeout(() => checkStep1Validity(), 0);
                    }}
                    onBlur={() => {
                      const value = form.getFieldValue("amount");
                      if (value) {
                        googleAnalytics.event({
                          category: "Donate Page - Donation Amount",
                          action: "Custom Amount Entered",
                          label: `${value}`,
                        });
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item name="hideAmount" valuePropName="checked">
                  <Checkbox className=" gray-8">
                    Hide my donation amount
                  </Checkbox>
                </Form.Item>

                <Group xs="horizontal" spacing={3} className="w-100">
                  <Form.Item
                    name="firstName"
                    className="flex-1"
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        message: "Please enter your first name.",
                      },
                    ]}
                  >
                    <Input
                      className="w-100"
                      size="large"
                      placeholder="First Name"
                      defaultValue={user?.firstName ? user?.firstName : ""}
                    />
                  </Form.Item>
                  <Form.Item
                    name="lastName"
                    className="flex-1"
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        message: "Please enter your last name.",
                      },
                    ]}
                  >
                    <Input
                      className="w-100"
                      size="large"
                      placeholder="Last Name"
                      defaultValue={user?.lastName ? user?.lastName : ""}
                    />
                  </Form.Item>
                </Group>
                {community.organization?.meta?.externalCampaign
                  ?.campaignCompanyFieldName && (
                  <Form.Item name="companyName">
                    <Input
                      className="w-100"
                      size="large"
                      placeholder={
                        community.organization?.meta?.externalCampaign
                          ?.campaignCompanyFieldName
                      }
                    />
                  </Form.Item>
                )}
                <Form.Item name="hideName" valuePropName="checked">
                  <Checkbox className=" gray-8">Hide my name</Checkbox>
                </Form.Item>

                {user?.email ? (
                  <>
                    {/* Hidden form field to ensure email is included in form values when user is logged in */}
                    <Form.Item name="email" hidden>
                      <Input type="hidden" />
                    </Form.Item>
                    <Text strong className="f-4 db">
                      Email Address
                    </Text>
                    <Text className="f-4 db mb-2">{user.email}</Text>
                  </>
                ) : (
                  <Form.Item
                    name="email"
                    hasFeedback
                    rules={[
                      {
                        type: "email",
                        message: "Please enter a valid email.",
                      },
                      {
                        required: true,
                        message: "Please enter an email.",
                      },
                    ]}
                  >
                    <Input
                      className="w-100"
                      size="large"
                      placeholder="Email Address"
                    />
                  </Form.Item>
                )}
                <Text className="f-3 gray-8 db pb-5">
                  Your email is required to send donation receipts and your
                  donation details (including name and email) will be shared
                  with {event.host}, the fundraiser leader, but will otherwise
                  remain private.
                  <br />
                  {user?.email ? (
                    <>
                      <Text strong>Not {user.email}?</Text>{" "}
                      <a
                        href="#"
                        onClick={handleLogoutClick}
                        className="primary"
                      >
                        Log out now
                      </a>
                    </>
                  ) : null}
                </Text>

                {event?.organization?.meta?.zipCodeStepOne && (
                  <Form.Item
                    name="zipCode"
                    hasFeedback
                    rules={[zipCodeValidationRule]}
                  >
                    <Input
                      className="w-100"
                      size="large"
                      maxLength={5}
                      placeholder="Zip Code"
                    />
                  </Form.Item>
                )}
                <Text strong className="f-3 db mb-2">
                  Why is this cause important to you (optional)
                </Text>
                <Form.Item name="donationNote">
                  <TextArea
                    className="w-100"
                    rows={4}
                    maxLength={MAX_TEXT_AREA_LENGTH}
                    showCount={true}
                    placeholder={
                      '"So glad you are raising funds and awareness for this because..."'
                    }
                  />
                </Form.Item>

                {/* ADD IN OPT-INs based on  externalCampaign.optInOption1 in org's meta */}
                {community?.organization?.meta?.externalCampaign
                  ?.optInOption1 && (
                  <Form.Item name="optIn1" valuePropName="checked">
                    <Checkbox className="">
                      {
                        community?.organization?.meta?.externalCampaign
                          ?.optInOption1
                      }
                    </Checkbox>
                  </Form.Item>
                )}

                {community?.organization?.meta?.externalCampaign
                  ?.optInOption2 && (
                  <Form.Item name="optIn2" valuePropName="checked">
                    <Checkbox className="">
                      {
                        community?.organization?.meta?.externalCampaign
                          ?.optInOption2
                      }
                    </Checkbox>
                  </Form.Item>
                )}
              </Group>
              <Divider className="mt-4 mb-5" />
              <Form.Item className="mb-0">
                <div className="flex justify-center pb-3">
                  <Button
                    type="primary"
                    disabled={!isStep1Valid}
                    style={{
                      borderRadius: "9999px",
                      width: "260px",
                      height: "48px",
                      ...(!isStep1Valid
                        ? {
                            backgroundColor: "#d9d9d9",
                            borderColor: "#d9d9d9",
                            color: "rgba(0, 0, 0, 0.25)",
                          }
                        : {}),
                    }}
                    className={
                      isStep1Valid
                        ? "ant-btn ant-btn-primary darkenPrimaryBkd bg-primary b-primary ttu f-3"
                        : "ttu f-3"
                    }
                    htmlType="submit"
                  >
                    NEXT <RightOutlined className="ml-3" />
                  </Button>
                </div>
              </Form.Item>
            </Form>
          </Card>
        )}
        {/* Grab Billing Info */}
        {step === 2 && (
          <Card
            className="w-100 md_w-50"
            style={{
              margin: "0 auto",
              boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
            }}
          >
            <NextAndPreviousBar
              nextText="Submit Donation"
              centerText={
                isBalanceOnly ? "Confirm Donation" : "Billing Information"
              }
              actionTextClass="f-4 fwb primary"
              centerTextClass="f-6 fwb"
              previousText="Back"
              showRight={!isBalanceOnly}
              previousFunction={handlePrevious}
              nextFunction={
                isBalanceOnly
                  ? handleBalanceOnlySubmit
                  : () => {
                      const form = document.getElementById(
                        "stripe-card-form"
                      ) as HTMLFormElement;
                      form?.requestSubmit();
                    }
              }
            />
            <Divider className="mt-3 mb-3" />
            <Group spacing={4} className="pa-5">
              <Title level={5}>Donation Summary</Title>
              <Divider className="mb-3" />
              {donationFromBalance > 0 && (
                <Group
                  xs="vertical"
                  sm="horizontal"
                  spacing={1}
                  className="sm_justify-between mb-4"
                >
                  <Text className="f-5">
                    Amount to be deducted from your account:
                  </Text>
                  <Text strong className="f-5">
                    ${new Decimal(donationFromBalance).toFixed(2)}
                  </Text>
                </Group>
              )}
              {canUseBuckets && donationFromCC > 0 && (
                <Group
                  xs="vertical"
                  sm="horizontal"
                  spacing={1}
                  className="sm_justify-between mb-4"
                >
                  <Text className="f-5">
                    Amount to be charged to your card:
                  </Text>
                  <Text strong className="f-5">
                    ${new Decimal(donationFromCC).toFixed(2)}
                  </Text>
                </Group>
              )}
              {!canUseBuckets && (
                <Group
                  xs="vertical"
                  sm="horizontal"
                  spacing={1}
                  className="sm_justify-between mb-4"
                >
                  <Text className="f-5">
                    Amount to be charged to your card:
                  </Text>
                  <Text strong className="f-5">
                    ${new Decimal(formValues?.amount || 0).toFixed(2)}
                  </Text>
                </Group>
              )}
              {isBalanceOnly ? (
                <>
                  <Text className="f-4 gray-7 db mb-4">
                    Your account balance fully covers this donation. No credit
                    card is needed.
                  </Text>
                  <Divider className="mt-4" />
                  <div className="flex justify-center mt-4">
                    <Button
                      type="primary"
                      style={{
                        borderRadius: "9999px",
                        width: "260px",
                        height: "48px",
                      }}
                      className="ant-btn ant-btn-primary darkenPrimaryBkd bg-primary b-primary ttu f-3"
                      loading={balanceOnlyLoading}
                      onClick={handleBalanceOnlySubmit}
                    >
                      SUBMIT DONATION
                    </Button>
                  </div>
                </>
              ) : (
                formValues && (
                  <StripeCardPayment
                    formData={formValues}
                    stripeAccount={event.campaign?.paymentGateway.publicKey}
                    event={event}
                    community={community}
                    setPaymentComplete={updatePaymentComplete}
                  />
                )
              )}
            </Group>
          </Card>
        )}
        {/* Success Step */}
        {step === 3 && (
          <Card
            className="w-100 md_w-50"
            style={{
              margin: "0 auto",
              boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
              padding: 0,
              overflow: "hidden",
            }}
            bodyStyle={{ padding: 0 }}
          >
            {/* Success Header */}
            <div
              className="flex bg-primary white pa-5"
              style={{
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Title level={3} className="white mb-0">
                Thank you for Donating!
              </Title>
            </div>

            <Group spacing={4} className="pa-5">
              {/* If org matched user funds and the matchFundsRemaining was still > 0 */}
              {event.campaign?.thresholdTrigger ===
                "one_to_one_match_trigger" &&
              event.campaign.matchFundsRemaining &&
              event.campaign?.matchFundsRemaining > 0 ? (
                <Text className="f-5 tc db">
                  {event.organization.name} matched your ${formValues?.amount}{" "}
                  {isBalanceOnly ? "donation" : "credit card donation"} with an
                  additional ${formValues?.amount}.
                </Text>
              ) : (event.campaign?.thresholdTrigger === "donation_trigger" ||
                  event.campaign?.thresholdTrigger ===
                    "tiered_match_trigger") &&
                event.campaign.matchFundsRemaining &&
                event.campaign?.matchFundsRemaining > 0 ? (
                <Text className="f-5 tc db">
                  Your {isBalanceOnly ? "donation" : "credit card donation"} is
                  helping unlock match grants from{" "}
                  {event.campaign.nextThreshold?.sponsorName ||
                    event.organization.name}
                  !
                </Text>
              ) : (
                <Text strong className="f-5 tc db">
                  Your ${formValues?.amount} donation brought us closer to our
                  goal!
                </Text>
              )}

              {/* DONATIONS MATCHED BY (Optional) */}
              {showMatch && matchLogo && (
                <>
                  <Group
                    xs="horizontal"
                    spacing={3}
                    className="justify-center items-center"
                  >
                    <Text className="f-1 gray-7">
                      {event.campaign?.thresholdTrigger ===
                        "one_to_one_match_trigger" ||
                      event.campaign?.thresholdTrigger === "donation_trigger" ||
                      event.campaign?.thresholdTrigger ===
                        "tiered_match_trigger"
                        ? "Donations matched by"
                        : "Donation fees covered by"}
                    </Text>
                    <div className="w-25">
                      <img
                        src={matchLogo}
                        alt="Sponsor Logo"
                        className="w-100"
                        style={{ objectFit: "contain" }}
                      />
                    </div>
                  </Group>
                  <Divider />
                </>
              )}

              {/* Share Section */}
              <Group className="w-100 md_w-80 self-center">
                {/* ShareBar at non-mobile sizes */}
                <div className="dn md_db">
                  <ShareBar
                    labeledButtons={true}
                    liftOnHover={true}
                    rectangularButtons={true}
                    clipboard={false}
                    onShare={handleShareClick}
                    analyticsProperties={shareAnalyticsProperties}
                  />
                </div>
                {/* ShareBar at mobile sizes */}
                <div className="w-50 md_dn mh-auto">
                  <ShareBar
                    rectangularButtons={true}
                    clipboard={false}
                    liftOnHover={true}
                    onShare={handleShareClick}
                    analyticsProperties={shareAnalyticsProperties}
                  />
                </div>
              </Group>

              {/* Back Button */}
              <div className="flex justify-center pt-3">
                <Button
                  type="primary"
                  style={{
                    borderRadius: "9999px",
                    width: "260px",
                    height: "48px",
                  }}
                  className="ant-btn ant-btn-primary darkenPrimaryBkd bg-primary b-primary ttu f-3"
                  onClick={handleFundraiserClick}
                >
                  BACK TO FUNDRAISER
                </Button>
              </div>
            </Group>
          </Card>
        )}
      </div>
    </>
  );
};

export default EventDonate;
